/* eslint-disable @typescript-eslint/member-ordering */
import * as React from "react";
import {action, computed, runInAction} from "mobx";
import {observer} from "mobx-react";

import {Kiwis, Station, DataCompleteness} from "../../../../../kiwis";
import {MapState} from "../../../../../core/map-state";
import {AppState} from "../../../app-mobx";

import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Collection from "@arcgis/core/core/Collection";

import {StationLayerConfiguration} from "../../../../../config/station-layer-configuration";
import {LayerFilter} from "../../../../common/LayerFilter";
import {Measurement} from "../../../../../kiwis/measurement";
import {UiState} from "../../../../../core/ui-state";

export interface StationLayerProps {
    store: Kiwis;
    mapStore: MapState;
    appStore: AppState;
    uiStore: UiState;
    config: StationLayerConfiguration;
}

@observer
export class StationLayer extends React.Component<StationLayerProps, any> {
    constructor(props: StationLayerProps) {
        super(props);
    }

    render(): JSX.Element | null {
        const {appStore, mapStore} = this.props;
        const featureLayer = this.featureLayer;

        if (!featureLayer || !mapStore.map) {
            return null;
        }

        // remove previously added layers
        const prevLayer = mapStore.map.findLayerById(featureLayer.id);
        if (prevLayer) {
            mapStore.map.remove(prevLayer);
        }
        mapStore.map.add(featureLayer);

        // register on mobx store to access it more easily
        mapStore.addLayer(featureLayer.id, featureLayer);

        // Add LayerFilter to separate filtering from rendering,
        // to avoid re-rendering of layer when filter is activated or changed.
        return <LayerFilter mapStore={mapStore}
            appStore={appStore}
            featureLayer={featureLayer} />;
    }

    @computed
    get features() {
        const features: Collection = new Collection();
        if (this.props.store.dataCompleteness !== DataCompleteness.MeasurementCode) {
            // return empty collection
            // stations need current measurements for symbols and labels
            return features;
        }

        this.props.store.stations.forEach((station: Station) => {
            // filter stations
            if (this.props.config.filter) {
                if (!this.props.config.filter.evaluate(station)) {
                    // exclude filtered stations
                    return;
                }
            }

            const attributes = {};
            // add configured fields from station to feature
            this.props.config.fields.forEach((field) => {
                const value = station[field.name];
                if (value) {
                    attributes[field.name] = value;
                } else {
                    // set value to null if property is undefined. Otherwise feature layer has to sanitize
                    attributes[field.name] = null;
                }
            });
            // add measurements, if present
            if (station.currentMeasurements) {
                // add current measurement values to the feature
                station.currentMeasurements.forEach((measurement: Measurement, key: string) => {
                    attributes[`${key}`] = measurement.value; // use absolute value
                });
            }
            const graphic = {
                geometry: {
                    type: "point",
                    x: station.x,
                    y: station.y,
                    spatialReference: { wkid: 2056 }
                },
                attributes
            };
            features.add(graphic);
        });
        return features;
    }

    getGraphic(featureLayer: __esri.FeatureLayer, response: any) {
        if (!featureLayer || !response || !response.results) {
            return null;
        }

        // eslint-disable-next-line arrow-body-style
        const layer = response.results.filter((result: any) => {
            return result?.graphic?.layer === featureLayer;
        })[0];
        if (!layer) {
            return null;
        }

        return layer.graphic;
    }

    /**
     * Change map cursor style
     *
     * @param style
     */
    changeCursor(style: string) {
        if (!this.props.mapStore.view) {
            return;
        }

        this.props.mapStore.view.container.style.cursor = style;
    }

    /**
     * Mouse over station feature
     *
     * @param featureLayer
     * @param screenPoint
     * @param response
     */
    @action
    onHover(featureLayer: __esri.FeatureLayer, screenPoint: any, response: any) {
        const {store, mapStore} = this.props;

        // get feature from topmost layer
        if (!response.results.length) {
            // this.changeCursor("default");
            mapStore.clearFeatureInfo();
            return;
        }

        const graphic = this.getGraphic(featureLayer, response);
        if (!graphic) {
            return;
        }
        // this.changeCursor("pointer");

        const stationOid = graphic.attributes.oid;
        const station = store.stations.get(stationOid);
        if (!station) {
            return;
        }

        const info = `${station.name} (${station.no})`;
        mapStore.setFeatureInfo(info);
    }

    /**
     * Click to station feature
     *
     * @param view
     * @param featureLayer
     * @param response
     */
    @action
    onClick(view: __esri.View, featureLayer: __esri.FeatureLayer, response: any) {
        const {mapStore, store, uiStore} = this.props;

        // get feature from topmost layer
        if (!response.results.length) {
            mapStore.unhighlightFeature();
            return;
        }

        const graphic = this.getGraphic(featureLayer, response);
        if (!graphic) {
            return;
        }

        const stationOid = graphic.attributes.oid;
        store.selectStation(stationOid);

        // open station popup directly
        uiStore.openStationPopup(1);

        view.whenLayerView(graphic.layer).then((layer: __esri.FeatureLayerView) => {
            runInAction(() => {
                mapStore.unhighlightFeature();

                const highlighted = layer.highlight(graphic);
                mapStore.highlightFeature(highlighted);
            });
        });
    }

    // the layer gets updated when stations in kiwis-store are updated
    @computed
    get featureLayer(): FeatureLayer | null {
        if (!this.features.length) {
            return null;
        }

        const {config, mapStore} = this.props;

        const layerConfig = config as __esri.FeatureLayerProperties;

        const featureLayer = new FeatureLayer({
            source: this.features,
            renderer: config.renderer as __esri.RendererProperties,
            ...layerConfig
        });

        // TODO: Move this to another class, because highlighting a feature is now triggering a render
        // of the whole layer
        featureLayer.when(() => {
            const view = mapStore.view;

            if (!view) {
                return;
            }

            view.on("click", (event) => {
                // the hitTest() checks to see if any graphics in the view
                // intersect the x, y coordinates of the pointer
                // fix pointer position when map does not fill up the whole screen
                // event.screenPoint.x += view.position[0];
                // event.screenPoint.y += view.position[1];

                view.hitTest(event).then((response) => {
                    this.onClick(view, featureLayer, response);
                });
            });

            let timer: any = null;
            // show station details on mouse hover
            view.on("pointer-move", (evt) => {
                if (timer) {
                    clearTimeout(timer);
                }

                // wait with hit test until mouse is not moving anymore
                timer = setTimeout(() => {
                    const screenPoint = {
                        x: evt.x,
                        y: evt.y
                    };
                    // the hitTest() checks to see if any graphics in the view
                    // intersect the given screen x, y coordinates
                    view.hitTest(screenPoint)
                        .then((response) => {
                            this.onHover(featureLayer, screenPoint, response);
                        });
                }, 500);
            });

        }, (error: Error) => {
            // eslint-disable-next-line no-console
            console.log(error);
        });

        return featureLayer;
    }

    componentDidMount(): void {}

}
